import { useEffect } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';

import CpdAutocomplete from '@/components/ui/CpdAutocomplete';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';

const FacilitiesAutocomplete = ({
  value = [],
  onChange,
  label = 'Select Facilities',
  placeholder = 'Search and select facilities...',
  size = 'small',
  fullWidth = true,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  multiple = true,
  organizationId,
  showBulkActions = true,
}) => {
  const { facilities, loading, error: apiError } = useGetFacilities({
    organizationId,
  });

  useEffect(() => {
    if (
      organizationId &&
      facilities[0] &&
      facilities[0].organization_id !== organizationId
    ) {
      onChange([]);
    }
  }, [organizationId]);

  const selectedFacilities = facilities.filter((facility) =>
    value.includes(facility.id),
  );

  const handleChange = (event, newValue) => {
    const facilityIds = newValue.map((facility) => facility.id);
    onChange(facilityIds);
  };

  const handleSelectAllClick = () => {
    if (facilities.length > 0) {
      const allFacilityIds = facilities.map((facility) => facility.id);
      onChange(allFacilityIds);
    }
  };

  const handleClearAllClick = () => {
    onChange([]);
  };

  const renderOption = (props, facility) => (
    <Box component='li' {...props}>
      <Box>
        <Typography variant='body2'>{facility.name}</Typography>
        <Typography variant='caption' color='text.secondary'>
          ID: {facility.id}
        </Typography>
      </Box>
    </Box>
  );

  const showWarning = !organizationId;

  return (
    <Box>
      {showWarning && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          <Typography variant='body2'>
            Please select an organization first to see available facilities.
          </Typography>
        </Alert>
      )}

      {showBulkActions &&
        multiple &&
        facilities.length > 0 &&
        !showWarning &&
        !loading && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size='small'
              variant='outlined'
              onClick={handleSelectAllClick}
              disabled={value.length === facilities.length || disabled}>
              Select All ({facilities.length})
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={handleClearAllClick}
              disabled={value.length === 0 || disabled}>
              Clear All
            </Button>
          </Box>
        )}

      <CpdAutocomplete
        multiple={multiple}
        options={showWarning ? [] : facilities}
        value={showWarning ? [] : selectedFacilities}
        onChange={showWarning ? () => {} : handleChange}
        label={label}
        placeholder={placeholder}
        size={size}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled || showWarning}
        error={error}
        helperText={
          showWarning
            ? 'Select an organization to enable facility selection'
            : helperText
        }
        loading={loading}
        apiError={apiError}
        getOptionLabel={(facility) => facility.name}
        getOptionValue={(facility) => facility.id}
        renderOption={renderOption}
        chipColor='primary'
        chipVariant='outlined'
      />

      {showBulkActions && multiple && !showWarning && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          {value.length} facilities selected
        </Typography>
      )}
    </Box>
  );
};

export default FacilitiesAutocomplete;
